import type { HistoryEntry } from "@/features/draw/types";

const DB_NAME = "avatar-generator";
const STORE_NAME = "history";
const DB_VERSION = 1;

// Helper function to make history entries serializable
const makeSerializable = (history: HistoryEntry[]): HistoryEntry[] => {
  return history.map((entry) => {
    // Create a new object with only the serializable properties
    const serializedEntry: HistoryEntry = {
      timestamp: entry.timestamp,
      layers: entry.layers.map((layer) => ({
        id: layer.id,
        name: layer.name,
        visible: layer.visible,
        pixels: new Uint32Array(layer.pixels),
        requiresPrevious: layer.requiresPrevious,
      })),
      prompt: entry.prompt,
      strength: entry.strength,
      lineWeight: entry.lineWeight,
      tool: entry.tool,
      imageUrl: entry.imageUrl,
      extractedPrompt: entry.extractedPrompt,
    };
    return serializedEntry;
  });
};

export const initDB = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error("Failed to open database"));
    };

    request.onsuccess = () => {
      resolve();
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "timestamp" });
      }
    };
  });
};

export const saveHistory = async (history: HistoryEntry[]): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      try {
        // Clear existing data
        store.clear();

        // Convert history to serializable format
        const serializableHistory = makeSerializable(history);

        // Save new history with timestamp
        const historyWithTimestamp = {
          timestamp: Date.now(),
          entries: serializableHistory,
        };

        store.add(historyWithTimestamp);

        transaction.oncomplete = () => {
          resolve();
        };
      } catch (error) {
        console.error("Failed to serialize history:", error);
        reject(new Error("Failed to serialize history data"));
      }

      transaction.onerror = () => {
        reject(new Error("Failed to save history"));
      };
    };

    request.onerror = () => {
      reject(new Error("Failed to open database"));
    };
  });
};

export const loadHistory = async (): Promise<HistoryEntry[]> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const results = getAllRequest.result;
        if (results.length > 0) {
          // Get the most recent history
          const latestHistory = results.reduce((latest, current) =>
            current.timestamp > latest.timestamp ? current : latest
          );

          // Reconstruct the history entries with proper Layer objects
          const reconstructedHistory = latestHistory.entries.map(
            (entry: HistoryEntry) => {
              // Check if imageUrl is a blob URL (which would be invalid after page reload)
              if (entry.imageUrl && entry.imageUrl.startsWith("blob:")) {
                // Replace with a placeholder or remove the imageUrl
                console.warn(
                  "Found invalid blob URL in history, replacing with placeholder"
                );
                entry.imageUrl =
                  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjAwIDIwMCI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNlZWUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIyMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgYWxpZ25tZW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+";
              }

              return {
                ...entry,
                layers: entry.layers.map((layer) => ({
                  ...layer,
                  pixels: Uint32Array.from(layer.pixels),
                })),
              };
            }
          );

          resolve(reconstructedHistory);
        } else {
          resolve([]);
        }
      };

      getAllRequest.onerror = () => {
        reject(new Error("Failed to load history"));
      };
    };

    request.onerror = () => {
      reject(new Error("Failed to open database"));
    };
  });
};

export const clearHistory = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      try {
        // Clear all data from the store
        const clearRequest = store.clear();

        clearRequest.onsuccess = () => {
          resolve();
        };

        clearRequest.onerror = () => {
          reject(new Error("Failed to clear history store"));
        };
      } catch (error) {
        console.error("Failed to clear history:", error);
        reject(new Error("Failed to clear history data"));
      }
    };

    request.onerror = () => {
      reject(new Error("Failed to open database"));
    };
  });
};
